import React, { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { Modal } from 'ggx-componentlibrary/components/modal/modal.component'
import { Button } from 'ggx-componentlibrary/components/button/button.component'
import Table from 'ggx-src/users-and-branches/users-tab/table/table.component'
import { LoadingSpinner } from 'ggx-src/legacy/views/helpers/loading/spinner'
import {
  BodyBold,
  Body,
} from 'ggx-componentlibrary/design/typography/typography.styles'
import {
  Footer,
  StyledPagination,
  StyledTable,
} from './address-book-modal.component.styles'
import { addressBookService } from '../api/api.service'

const PAGE_SIZE = 6

const AddressBookModal = ({ onSelect, onClose }) => {
  const [t] = useTranslation()
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ page: 0, totalPage: 0 })
  const [data, setData] = useState([])
  const columns = useMemo(() => {
    const renderCellWithDefaultValue = value => (
      <Body typographyStyles="mb-none">{value || '-'}</Body>
    )
    return [
      {
        key: 'nickname',
        label: t('nickname'),
        width: 128,
        renderCell: renderCellWithDefaultValue,
      },
      {
        key: 'contact_name',
        label: t('contact_person'),
        width: 150,
        renderCell: renderCellWithDefaultValue,
      },
      {
        key: 'address',
        label: t('street_address'),
        width: 350,
        renderCell: renderCellWithDefaultValue,
      },
      {
        key: 'detail_address',
        label: t('myaccount_tab_company_placeholder_floor'),
        width: 110,
        renderCell: renderCellWithDefaultValue,
      },
      {
        key: 'contact_phone_number',
        label: t('common__phone_number'),
        width: 120,
        renderCell: renderCellWithDefaultValue,
      },
    ]
  }, [])

  const fetchPage = async (page = 1, forceLoading) => {
    if (!forceLoading && loading) return
    setLoading(true)
    try {
      const data = await addressBookService.getList()
      setPagination({
        page,
        totalPage: Math.ceil(data.length / PAGE_SIZE),
      })
      setData(
        data
          .map(item => {
            const matches = item.geo_location
              .substring(7, item.geo_location.length - 1)
              .split(' ')
            return {
              id: item.id,
              nickname: item.nickname,
              contact_name: item.contact_name,
              contact_phone_number: item.phone_number,
              address: item.street_address,
              detail_address: item.address_detail,
              email: item.email,
              lat: Number(matches[1]),
              lon: Number(matches[0]),
            }
          })
          .sort((a, b) => a.id - b.id)
      )
    } catch (error) {
      console.error(error)
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchPage(1, true)
  }, [])

  return (
    <Modal
      header={t('address_book')}
      footer={useMemo(
        () => (
          <Footer>
            {pagination.totalPage === 0 ? (
              <div style={{ flexGrow: 1 }}></div>
            ) : (
              <StyledPagination
                paginationText={t('orderManagement.table.pagination', {
                  currentPageNumber: pagination.page,
                  totalPageNumber: pagination.totalPage,
                })}
                currentPageNumber={pagination.page}
                totalPageNumber={pagination.totalPage}
                onPageNavClick={page => {
                  setPagination(val => ({ ...val, page }))
                }}
                loading={loading}
              />
            )}
            <Button buttonType="secondary" size="small" onClick={onClose}>
              {t('common__cancel')}
            </Button>
          </Footer>
        ),
        [pagination.page, pagination.totalPage, loading, onClose]
      )}
      onRequestClose={onClose}
      width="906px"
      height="625px"
      maxHeight="625px"
    >
      <StyledTable
        uniqueIdentifier="id"
        columns={columns}
        data={data.slice(
          (pagination.page - 1) * PAGE_SIZE,
          (pagination.page - 1) * PAGE_SIZE + PAGE_SIZE
        )}
        isLoading={loading}
        loadingView={<LoadingSpinner />}
        emptyView={
          <div>
            <BodyBold style={{ textAlign: 'center', marginBottom: 10 }}>
              {t('no_saved_address__title')}
            </BodyBold>
            <Body typographyStyles="mb-none" style={{ textAlign: 'center' }}>
              {t('no_saved_address__description')}
            </Body>
          </div>
        }
        onClickTr={onSelect}
      />
    </Modal>
  )
}

AddressBookModal.propTypes = {
  onSelect: PropTypes.func,
  onClose: PropTypes.func,
}

export { AddressBookModal }
